// TanStack Query hooks over the host `settingsApi`.
//
// Two durable, Playbook BE-owned preferences (presentation, launcher) and the
// mock-backed sections (Player AI general, Notifications) share one client.
// State survives navigation between sections (query cache, not component-local
// `useState`). Every key is scoped by tenant + user so a tenant/session switch
// can never read or persist another scope's cache.

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from "@tanstack/react-query";
import { useCallback, useRef, useState } from "react";
import { useAuthStore } from "../auth/auth-store.js";
import { isPreferenceConflict, settingsApi } from "../api/settings-client.js";
import type {
  LauncherPrefs,
  PresentationPrefs,
  ReplaceLauncherPrefs,
  ReplacePresentationPrefs,
  SettingsData,
  SettingsPatch,
} from "./types.js";

export function settingsQueryKey(
  tenantId: string | null | undefined,
  userId: string | undefined,
): readonly unknown[] {
  return ["settings", tenantId ?? "none", userId ?? "anon"] as const;
}

export function presentationPreferencesQueryKey(
  tenantId: string | null | undefined,
  userId: string | undefined,
): readonly unknown[] {
  return [
    "platform-preferences",
    "presentation",
    tenantId ?? "none",
    userId ?? "anon",
  ] as const;
}

export function launcherPreferencesQueryKey(
  tenantId: string | null | undefined,
  userId: string | undefined,
): readonly unknown[] {
  return [
    "platform-preferences",
    "launcher",
    tenantId ?? "none",
    userId ?? "anon",
  ] as const;
}

function useScope(): {
  tenantId: string | null | undefined;
  userId: string | undefined;
} {
  const tenantId = useAuthStore((s) => s.claims?.tenantId);
  const userId = useAuthStore((s) => s.claims?.userId);
  return { tenantId, userId };
}

export function usePresentationPreferencesQuery(): UseQueryResult<PresentationPrefs> {
  const { tenantId, userId } = useScope();
  return useQuery({
    queryKey: presentationPreferencesQueryKey(tenantId, userId),
    queryFn: () => settingsApi.getPresentationPreferences(),
    enabled: Boolean(tenantId && userId),
    retry: false,
  });
}

export function useUpdatePresentationPreferences() {
  const queryClient = useQueryClient();
  const { tenantId, userId } = useScope();
  const key = presentationPreferencesQueryKey(tenantId, userId);
  return useMutation<PresentationPrefs, Error, ReplacePresentationPrefs>({
    mutationFn: (input) => settingsApi.replacePresentationPreferences(input),
    onSuccess: (authoritative) => {
      queryClient.setQueryData<PresentationPrefs>(key, (current) =>
        !current || authoritative.version >= current.version
          ? authoritative
          : current,
      );
    },
    onError: () => {
      void queryClient.invalidateQueries({ queryKey: key });
    },
  });
}

export function useLauncherPreferencesQuery(): UseQueryResult<LauncherPrefs> {
  const { tenantId, userId } = useScope();
  return useQuery({
    queryKey: launcherPreferencesQueryKey(tenantId, userId),
    queryFn: () => settingsApi.getLauncherPreferences(),
    enabled: Boolean(tenantId && userId),
    retry: false,
  });
}

/** The three editable launcher fields; the version is composed by the hook. */
export type LauncherEdit = Omit<ReplaceLauncherPrefs, "expectedVersion">;

/**
 * Serialized compare-and-swap writer for the launcher preference.
 *
 * `update(updater)` applies the edit to the cache immediately (so rapid
 * reorder/add/remove clicks feel instant and compose), then queues ONE write
 * per edit behind the previous one. Each write sends the LATEST optimistic
 * state with the version the previous write came back with, so a burst of
 * edits never races itself and a newer queued edit is carried along rather
 * than overwritten. A success merges only the server's version metadata (the
 * fields already match what was sent or are newer).
 *
 * If another tab saved first (409), the hook refetches the authoritative row and
 * re-sends the same desired state ONCE against the fresh version: the user's own
 * edit wins over their other tab, which is the only sensible outcome for a
 * personal preference. A second failure, or any other error, is exposed as
 * `error` (and the cache is reconciled to the server) so the page can say so
 * instead of losing the edit silently.
 */
export function useUpdateLauncherPreferences(): {
  /** Applies the edit at once and resolves `true` once it is saved, `false` when the write failed. */
  readonly update: (
    updater: (current: LauncherPrefs) => LauncherEdit,
  ) => Promise<boolean>;
  readonly isPending: boolean;
  readonly error: Error | null;
} {
  const queryClient = useQueryClient();
  const { tenantId, userId } = useScope();
  const key = launcherPreferencesQueryKey(tenantId, userId);
  const queue = useRef<Promise<void>>(Promise.resolve());
  const [error, setError] = useState<Error | null>(null);
  const mutation = useMutation<LauncherPrefs, Error, ReplaceLauncherPrefs>({
    mutationFn: (input) => settingsApi.replaceLauncherPreferences(input),
    onSuccess: (authoritative) => {
      queryClient.setQueryData<LauncherPrefs>(key, (current) =>
        !current || authoritative.version >= current.version
          ? {
              ...(current ?? authoritative),
              version: authoritative.version,
              updatedAt: authoritative.updatedAt,
            }
          : current,
      );
    },
  });
  const { mutateAsync } = mutation;

  const update = useCallback(
    (updater: (current: LauncherPrefs) => LauncherEdit): Promise<boolean> => {
      const current = queryClient.getQueryData<LauncherPrefs>(key);
      if (!current) return Promise.resolve(false);
      queryClient.setQueryData<LauncherPrefs>(key, {
        ...current,
        ...updater(current),
      });
      const send = (state: LauncherPrefs, version: number) =>
        mutateAsync({
          favourites: state.favourites,
          moduleRosterConfigured: state.moduleRosterConfigured,
          showMoreApps: state.showMoreApps,
          defaultLanding: state.defaultLanding,
          expectedVersion: version,
        });
      const outcome: Promise<boolean> = queue.current
        .then(async () => {
          // Send the LATEST optimistic state, not the snapshot taken when this
          // edit was queued: a newer queued edit is then carried along instead
          // of being overwritten, and its own write becomes a no-op.
          const snapshot = queryClient.getQueryData<LauncherPrefs>(key);
          if (!snapshot) return;
          try {
            await send(snapshot, snapshot.version);
            setError(null);
          } catch (first) {
            if (!isPreferenceConflict(first)) throw first;
            // Lost a race with another tab: read the authoritative row WITHOUT
            // writing it into the cache (an edit made while this read is in
            // flight must survive), then keep every optimistic edit made up to
            // now, take the authoritative version, and re-apply once.
            const fresh = await settingsApi.getLauncherPreferences();
            const optimistic =
              queryClient.getQueryData<LauncherPrefs>(key) ?? snapshot;
            const reconciled: LauncherPrefs = {
              ...optimistic,
              version: fresh.version,
              updatedAt: fresh.updatedAt,
            };
            queryClient.setQueryData<LauncherPrefs>(key, reconciled);
            await send(reconciled, fresh.version);
            setError(null);
          }
        })
        .then(
          () => true,
          async (failure: unknown) => {
            setError(
              failure instanceof Error ? failure : new Error(String(failure)),
            );
            await queryClient.invalidateQueries({ queryKey: key });
            return false;
          },
        );
      // The queue itself always settles, so a later edit is never blocked by
      // an earlier failure.
      queue.current = outcome.then(() => undefined);
      return outcome;
    },
    [key, mutateAsync, queryClient],
  );

  return { update, isPending: mutation.isPending, error };
}

export function useSettingsQuery(): UseQueryResult<SettingsData> {
  const { tenantId, userId } = useScope();
  return useQuery({
    queryKey: settingsQueryKey(tenantId, userId),
    queryFn: () => settingsApi.getSettings(),
  });
}

/**
 * An updater builds a coarse patch (any subset of the mock sections) from the
 * *current* settings. Returning the updater (not a prebuilt patch) is what makes
 * rapid same-section edits safe: the optimistic cache write is applied
 * synchronously from the freshest cache, so a second edit composes onto the
 * first instead of clobbering a render-time snapshot.
 */
export type SettingsUpdater = (current: SettingsData) => SettingsPatch;

/**
 * Returns `update(updater)`. The updater runs once, against the freshest cache;
 * only the section(s) it returns are persisted (so a personal-pref toggle never
 * rewrites workspace config, and vice versa). On error the single change rolls
 * back.
 */
export function useUpdateSettings(): (updater: SettingsUpdater) => void {
  const queryClient = useQueryClient();
  const { tenantId, userId } = useScope();
  const key = settingsQueryKey(tenantId, userId);

  const mutation = useMutation<SettingsData, Error, SettingsPatch>({
    mutationFn: (patch) => settingsApi.updateSettings(patch),
  });

  return (updater: SettingsUpdater): void => {
    const current = queryClient.getQueryData<SettingsData>(key);
    if (!current) return;
    const patch = updater(current);
    // Synchronous optimistic write from the freshest cache — sequential edits
    // compose because each handler reads the prior write before computing.
    queryClient.setQueryData<SettingsData>(key, { ...current, ...patch });
    mutation.mutate(patch, {
      // On failure, refetch the authoritative state rather than restoring this
      // call's pre-snapshot — a stale snapshot could clobber a concurrent
      // successful edit (A fails after B succeeds). Refetch reconciles to truth.
      onError: () => {
        void queryClient.invalidateQueries({ queryKey: key });
      },
    });
  };
}
